/**
 * versona_rig.ts — the NEAR-SKINNED versona layer (rig wave, 2026-09-05).
 *
 * The far crowd stays what it was: one InstancedMesh per body, a matrix per
 * versona, whole-body procedural poses. The nearest few versonas (≤ MAX_ACTORS
 * inside NEAR_M) are drawn instead as real SkinnedMesh actors cloned from the
 * body's RIGGED master (`avatar-rig:<key>`, Meshy rig + the shared clip set),
 * each with its own AnimationMixer, cross-fading between clips chosen by the
 * pure map in versona_clips.ts. The crowd hides the instance it replaces.
 *
 * Why not the engine's GPU crowd skinning: CrowdModule logs itself as a
 * scaffolded stub (bind pose only). An animated LOD ring around the player is
 * how most crowds ship anyway — skinning cost stays bounded by MAX_ACTORS,
 * not by the 2,000 residents.
 *
 * Actors hang under the crowd's floating-origin anchor group in ANCHOR-LOCAL
 * coordinates (lib/local_origin.ts): world x/z minus the anchor.
 */
import * as THREE from 'three/webgpu';
import { applyDualQuaternionSkinning } from 'enginex/animation/dq/DualQuaternionSkinningNode.js';
import { stage4Stats } from './stage4_fx.js';
import { clone as skeletonClone } from 'three/addons/utils/SkeletonUtils.js';
import { renderAnchorX, renderAnchorZ } from './local_origin.js';
import { clipFor, type VersonaClip, type SeatKind } from './versona_clips.js';
import { activePreset } from './quality_tiers.js';
import { headTurn, HEAD_TURN_EASE } from './versona_gaze.js';

export const NEAR_M = 38;
export const MAX_ACTORS = 24;
export const FADE_S = 0.22;
/** Meshy rigs face +Z in model space, the crowd's facing axis — no turn. */
export const RIG_YAW_OFFSET = 0;

export interface RigPose {
  x: number; y: number; z: number; yaw: number;
  state: string; moving: boolean; talking: boolean; sprinting: boolean;
  phase: number; parity: number; winding: boolean; hurt: boolean;
  /** THE MELEE BEAT (2026-09-19): mid-swing in a versona-vs-versona fight, which writes no telegraph. */
  swinging?: boolean;
  /** npc_agent.job — selects the trade's task clip while working (task clips, 2026-09-10) */
  job?: string;
  /** An elder shuffles when they walk (clip wave 3). */
  elder?: boolean;
  /** HEAD LAYER (2026-09-15): the world yaw the head looks toward (a glance, a partner, a blast), or null. */
  lookYaw?: number | null;
  /** SOCIETY WAVE: what the station the versona is seated at puts UNDER them (a seated / lying clip needs it). */
  seat?: SeatKind;
  /** …and the tool their activity swings (drawn in the right hand; the clip grammar swings only a drawn tool). */
  handProp?: HandProp | null;
  /** True when the caller draws a carried crate for this versona (to_market, carting). */
  carrying?: boolean;
}

/** Tools the rig layer can put in a hand. */
export type HandProp = 'axe' | 'hammer' | 'hoe' | 'broom' | 'pole' | 'bucket';

export interface RigActor {
  id: string;
  bodyKey: string;
  root: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  clips: Map<string, THREE.AnimationClip>;
  current: THREE.AnimationAction | null;
  currentName: string;
  lastSeen: number;
  /** Cadence bookkeeping (see VersonaRigLayer.update). */
  bankedDt?: number;
  phase: number;
  /**
   * The actor's last posed position in ABSOLUTE world coordinates — the same
   * space the caller's player position is in. `root.position` is anchor-local
   * and `getWorldPosition` adds the FP64 rebase offset, so neither can be
   * compared against an absolute player position directly (see update).
   */
  absX: number;
  absZ: number;
  /** HEAD LAYER: the head bone (found by name), the current head turn off the body (rad), the wanted look. */
  head: THREE.Object3D | null;
  headYaw: number;
  bodyYaw: number;
  lookYaw: number | null;
  /** HAND PROP (society wave): the right-hand bone (by name), and what it holds now. */
  hand?: THREE.Object3D | null;
  handProp?: HandProp | null;
  handMesh?: THREE.Object3D | null;
}
type Actor = RigActor;

// ── Hand props: one shared mesh source per tool (cloned per actor; geometry + material are never disposed) ──
// ONE CONVENTION: the haft runs along bone-local +Y from the GRIP at the origin. If a real-GPU picture
// shows the tools on the wrong axis, `HAND_PROP_ROT` turns every one of them at once.
const HAND_PROP_ROT = new THREE.Euler(0, 0, 0);
const handSources = new Map<HandProp, THREE.Group>();
const std = (color: number, roughness: number, metalness = 0, emissive = 0): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color, roughness, metalness, emissive, emissiveIntensity: emissive ? 1.6 : 0 });
function buildHandSource(kind: HandProp): THREE.Group {
  const wood = std(0x6b4a2c, 0.9), steel = std(0xb8c0c8, 0.4, 0.7);
  const g = new THREE.Group();
  const haft = (len: number, grip: number, r = 0.018): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 1.2, len, 6), wood);
    m.position.y = len / 2 - grip;                            // the grip sits `grip` metres up from the butt
    return m;
  };
  switch (kind) {
    case 'axe': {
      const head = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.13, 0.03), steel);
      head.position.set(0.09, 0.62, 0);
      g.add(haft(0.78, 0.09), head);
      break;
    }
    case 'hammer': {
      const head = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.06, 0.06), steel);
      head.position.set(0, 0.3, 0);
      g.add(haft(0.4, 0.08), head);
      break;
    }
    case 'hoe': {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.02, 0.1), steel);
      blade.position.set(0.07, 1.02, 0);
      g.add(haft(1.2, 0.18), blade);
      break;
    }
    case 'broom': {
      const bristles = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.2, 0.06), std(0xb79a5a, 1));
      bristles.position.set(0, -0.72, 0);                     // the head hangs BELOW the grip, toward the street
      const h = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 1.3, 6), wood);
      h.position.y = -0.02;
      g.add(h, bristles);
      break;
    }
    case 'pole': {                                            // the lamplighter's pole: a wick burning at its tip
      const flame = new THREE.Mesh(new THREE.SphereGeometry(0.035, 8, 6), std(0xffc36b, 0.4, 0, 0xffa030));
      flame.position.set(0, 1.78, 0);
      g.add(haft(1.9, 0.15, 0.014), flame);
      break;
    }
    case 'bucket': {                                          // a pail on its bail, hanging from the fist
      const pail = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.1, 0.22, 10, 1, true), std(0x7a6a55, 0.8, 0.2));
      pail.material.side = THREE.DoubleSide;
      pail.position.y = -0.3;
      const base = new THREE.Mesh(new THREE.CircleGeometry(0.1, 10), pail.material);
      base.rotation.x = -Math.PI / 2; base.position.y = -0.41;
      const water = new THREE.Mesh(new THREE.CircleGeometry(0.12, 10), std(0x3f6f9a, 0.15, 0.1));
      water.rotation.x = -Math.PI / 2; water.position.y = -0.23;
      const bail = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.006, 4, 10, Math.PI), steel);
      bail.position.y = -0.19;
      g.add(pail, base, water, bail);
      break;
    }
  }
  g.traverse((o) => { o.userData.sharedAsset = true; (o as THREE.Mesh).castShadow = true; o.frustumCulled = false; });
  return g;
}
function handMeshFor(kind: HandProp): THREE.Object3D {
  let src = handSources.get(kind);
  if (!src) { src = buildHandSource(kind); handSources.set(kind, src); }
  const m = src.clone();
  m.rotation.copy(HAND_PROP_ROT);
  return m;
}
const _ws = new THREE.Vector3();

/** Put a tool in (or take it out of) an actor's right hand. Idempotent; a rig with no hand bone simply holds nothing. */
export function setRigHandProp(a: RigActor, kind: HandProp | null): boolean {
  if ((a.handProp ?? null) === kind) return !!a.handMesh;
  if (a.handMesh) { a.handMesh.parent?.remove(a.handMesh); a.handMesh = null; }
  a.handProp = kind;
  if (!kind) return false;
  if (a.hand === undefined) {
    let hand: THREE.Object3D | null = null;
    a.root.traverse((o) => { if (!hand && /(^|[:_.])righthand$/i.test(o.name)) hand = o; });
    a.hand = hand;
  }
  if (!a.hand) return false;
  const mesh = handMeshFor(kind);
  // The rig's bones carry Meshy's centimetre scale: cancel the bone's WORLD scale so the tool is life-size.
  a.hand.updateWorldMatrix(true, false);
  a.hand.getWorldScale(_ws);
  mesh.scale.setScalar(1 / Math.max(1e-6, _ws.x));
  a.hand.add(mesh);
  a.handMesh = mesh;
  return true;
}
const _axis = new THREE.Vector3();
const _q = new THREE.Quaternion();

/**
 * RIG ACTORS ARE CULLED (2026-09-20). They were `frustumCulled = false`, so every ~290k-triangle skin was drawn
 * into the main pass even behind the camera AND into EVERY shadow cascade (three culls per render: an un-cullable
 * caster lands in all four maps, 3 of which it is nowhere near) — in a festival crowd, half the actors stand
 * behind you. The flag was off because a SkinnedMesh's `boundingSphere` starts null and three's first frustum test
 * then CPU-skins every vertex to build one (~290k × the bone maths, per actor). So it is HANDED a sphere: the
 * geometry's own rest sphere (the T-pose — mesh-local space, which in 'attached' bind mode follows the root),
 * widened so a lying or knocked-down body (head ~1.8 m from the root) still fits. Conservative: a sphere that is
 * too big only draws a body that was not needed; it can never drop one that was.
 */
export const RIG_CULL_MARGIN = 1.8;
const rigSpheres = new WeakMap<THREE.BufferGeometry, THREE.Sphere>();
export function rigCullSphere(geometry: THREE.BufferGeometry): THREE.Sphere {
  let s = rigSpheres.get(geometry);
  if (!s) {
    if (!geometry.boundingSphere) geometry.computeBoundingSphere(); // once per BODY (clones share the geometry)
    const g = geometry.boundingSphere;
    s = g && Number.isFinite(g.radius) && g.radius > 0 ? new THREE.Sphere(g.center.clone(), g.radius * RIG_CULL_MARGIN) : new THREE.Sphere(new THREE.Vector3(), Infinity);
    rigSpheres.set(geometry, s);
  }
  return s;
}
/** `localStorage atlas:rig-cull = 'off'` restores the un-culled actors (read once per page). */
let rigCullOn: boolean | null = null;
function rigCullEnabled(): boolean {
  if (rigCullOn === null) { try { rigCullOn = localStorage.getItem('atlas:rig-cull') !== 'off'; } catch { rigCullOn = true; } }
  return rigCullOn;
}

/** A skinned actor cloned from a rig master (SkeletonUtils clone: geometry shared, skeleton own). */
export function makeRigActor(master: THREE.Object3D, bodyKey: string, id: string): RigActor {
  const root = skeletonClone(master);
  root.name = `versona-rig:${bodyKey}`;
  const cull = rigCullEnabled();
  root.traverse((o) => {
    (o as THREE.Mesh).castShadow = true;
    const sm = o as THREE.SkinnedMesh;
    if (cull && sm.isSkinnedMesh && sm.geometry) { sm.boundingSphere = rigCullSphere(sm.geometry); sm.frustumCulled = true; }
    else o.frustumCulled = false;
  });
  // Stage 4: dual-quaternion skinning on the actor's materials (bound per
  // rendered object, so a body's actors keep sharing the master material —
  // patched once per material).
  if (activePreset().dqSkinning) {
    root.traverse((o) => {
      const sm = o as THREE.SkinnedMesh;
      if (!sm.isSkinnedMesh) return;
      for (const mat of (Array.isArray(sm.material) ? sm.material : [sm.material])) {
        const mm = mat as THREE.Material & { userData: Record<string, unknown> };
        if (mm.userData.dqSkinning) continue;
        try { applyDualQuaternionSkinning(mm as never, sm); mm.userData.dqSkinning = true; stage4Stats.dqActors++; } catch { /* best-effort */ }
      }
    });
  }
  const mixer = new THREE.AnimationMixer(root);
  const clips = new Map<string, THREE.AnimationClip>();
  for (const c of master.animations ?? []) clips.set(c.name, c);
  let ph = 0; for (let i = 0; i < id.length; i++) ph = (ph * 31 + id.charCodeAt(i)) | 0;
  // absX/absZ are overwritten by the first pose(); NaN would make the cadence
  // test fall through to the far branch before that, so start them at the
  // origin (a fresh actor is posed the same frame it is acquired).
  // The head bone by name (the Meshy/Mixamo rig: 'Head' / 'mixamorig:Head'); null = no head layer.
  let head: THREE.Object3D | null = null;
  root.traverse((o) => { if (!head && /(^|[:_.])head$/i.test(o.name)) head = o; });
  return { id, bodyKey, root, mixer, clips, current: null, currentName: '', lastSeen: 0, phase: Math.abs(ph) % 3, absX: 0, absZ: 0, head, headYaw: 0, bodyYaw: 0, lookYaw: null };
}

/** Cross-fade an actor to a clip (walk/run ride the rig under Meshy's names; anything missing falls to idle). */
export function playRigClip(a: RigActor, name: VersonaClip, loop: boolean, speed: number, stats?: { missingClip: number }): void {
  if (a.currentName === name) { if (a.current) a.current.timeScale = speed; return; }
  let clip = a.clips.get(name);
  if (!clip) {
    const alt = name === 'Walking' ? ['Walking_Woman', 'Casual_Walk', 'walk', 'Walk'] : name === 'Running' ? ['Run_02', 'RunFast', 'run', 'Run'] : [];
    for (const n of alt) { const c = a.clips.get(n); if (c) { clip = c; break; } }
    if (!clip) { if (stats) stats.missingClip++; clip = a.clips.get('Idle') ?? [...a.clips.values()][0]; }
    if (!clip) return;
  }
  const next = a.mixer.clipAction(clip);
  next.reset();
  next.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
  next.clampWhenFinished = !loop;
  next.timeScale = speed;
  next.enabled = true;
  if (a.current) { a.current.crossFadeTo(next, FADE_S, false); next.play(); }
  else next.play();
  a.current = next;
  a.currentName = name;
}

export class VersonaRigLayer {
  private readonly masters = new Map<string, THREE.Object3D>();
  private readonly actors = new Map<string, Actor>();
  private readonly pool = new Map<string, Actor[]>();
  private owned = new Set<string>();
  /** Body keys of versonas inside the ring whose rig master is NOT loaded yet, nearest first —
   *  the streaming order for loadRigMasters (2026-09-06: the masters loaded in catalogue order,
   *  so the bodies standing next to the player wore their far twins for minutes). */
  private wanted: string[] = [];
  private lastT = -1;
  readonly stats = { actors: 0, bodies: 0, swaps: 0, missingClip: 0, posed: 0 , heads: 0 };

  constructor(private readonly group: THREE.Group) {}

  /** Register a body's rigged master (once per avatar key). */
  setMaster(bodyKey: string, master: THREE.Object3D): void {
    this.masters.set(bodyKey, master);
    this.stats.bodies = this.masters.size;
  }
  hasMaster(bodyKey: string): boolean { return this.masters.has(bodyKey); }
  /** Bodies wanted by versonas near the player (within 2× the ring) that have no master yet, nearest first. */
  wantedKeys(): readonly string[] { return this.wanted; }

  /** Which versonas this layer draws THIS frame: the nearest inside NEAR_M
   *  whose body has a rig, capped at MAX_ACTORS. Call before the pose loop. */
  plan(cands: ReadonlyArray<{ id: string; bodyKey: string; x: number; z: number }>, px: number, pz: number): Set<string> {
    const near: Array<{ id: string; bodyKey: string; d: number }> = [];
    const r2 = NEAR_M * NEAR_M;
    const missing = new Map<string, number>(); // bodyKey → nearest d² among versonas without a master
    for (const c of cands) {
      const d = (c.x - px) ** 2 + (c.z - pz) ** 2;
      if (!this.masters.has(c.bodyKey)) { if (d < r2 * 4 && d < (missing.get(c.bodyKey) ?? Infinity)) missing.set(c.bodyKey, d); continue; }
      if (d < r2) near.push({ id: c.id, bodyKey: c.bodyKey, d });
    }
    near.sort((a, b) => a.d - b.d);
    this.wanted = [...missing.entries()].sort((a, b) => a[1] - b[1]).map((e) => e[0]);
    // Wave 2 (2026-09-06): the actors are ~290k-triangle skins, so the tier caps how many are drawn
    // (potato 4 … ultra 24); a stored custom preset from before the field existed falls back to the max.
    const cap = Math.min(MAX_ACTORS, Math.max(0, activePreset().nearActors ?? MAX_ACTORS));
    const owned = new Set<string>();
    for (let i = 0; i < near.length && i < cap; i++) owned.add(near[i].id);
    // Release actors that left the ring; they return to the body's pool.
    for (const [id, a] of this.actors) {
      if (owned.has(id)) continue;
      this.actors.delete(id);
      this.group.remove(a.root);
      a.mixer.stopAllAction();
      a.current = null; a.currentName = '';
      setRigHandProp(a, null); // a pooled body goes back empty-handed
      const pool = this.pool.get(a.bodyKey) ?? [];
      if (pool.length < MAX_ACTORS) pool.push(a);
      this.pool.set(a.bodyKey, pool);
    }
    this.owned = owned;
    this.stats.actors = this.actors.size;
    return owned;
  }

  owns(id: string): boolean { return this.owned.has(id); }

  /** Place + animate one owned versona for this frame (anchor-local placement). */
  pose(id: string, bodyKey: string, p: RigPose): void {
    let a: Actor | null | undefined = this.actors.get(id);
    if (!a || a.bodyKey !== bodyKey) {
      if (a) { this.group.remove(a.root); this.actors.delete(id); }
      a = this.acquire(id, bodyKey);
      if (!a) return;
      this.actors.set(id, a);
      this.stats.swaps++;
    }
    a.lastSeen = performance.now();
    a.absX = p.x; a.absZ = p.z; // absolute — the cadence test compares against the player in this space
    a.root.position.set(p.x - renderAnchorX(), p.y, p.z - renderAnchorZ());
    a.root.rotation.set(0, p.yaw + RIG_YAW_OFFSET, 0);
    a.bodyYaw = p.yaw; a.lookYaw = p.lookYaw ?? null;
    // NEVER MIME: the grammar is told what is REALLY drawn — the seat under them, the tool in their hand, the crate in their arms.
    const held = setRigHandProp(a, p.handProp ?? null);
    const choice = clipFor(p.state, { moving: p.moving, talking: p.talking, sprinting: p.sprinting, phase: p.phase, parity: p.parity, winding: p.winding, hurt: p.hurt, job: p.job, seat: p.seat ?? null, handProp: held || !!p.carrying, elder: p.elder, swinging: p.swinging });
    this.play(a, choice.clip, choice.loop, choice.speed);
    this.stats.posed++;
  }

  private acquire(id: string, bodyKey: string): Actor | null {
    const pool = this.pool.get(bodyKey);
    let a = pool?.pop() ?? null;
    if (!a) {
      const master = this.masters.get(bodyKey);
      if (!master) return null;
      a = makeRigActor(master, bodyKey, id);
    }
    a.id = id;
    this.group.add(a.root);
    return a;
  }

  private play(a: Actor, name: VersonaClip, loop: boolean, speed: number): void { playRigClip(a, name, loop, speed, this.stats); }

  /**
   * Advance every mixer; call once per frame after the poses. ANIMATION
   * CADENCE BY DISTANCE (perf frontier 2026-09-08): a 290k-vertex skin is
   * re-skinned every frame its mixer advances, so actors past 15 m advance
   * every 2nd frame and past 30 m every 3rd, with the skipped time banked so
   * the clip never drifts — at 60 Hz that is 30/20 Hz animation on bodies
   * that cover a few dozen pixels.
   */
  update(nowS: number, playerX = 0, playerZ = 0): void {
    const dt = this.lastT < 0 ? 0 : Math.min(0.1, nowS - this.lastT);
    this.lastT = nowS;
    if (dt <= 0) return;
    this.frame++;
    this.stats.heads = 0;
    for (const a of this.actors.values()) {
      // ABSOLUTE vs ABSOLUTE. WHY (2026-09-14): this used
      // `root.getWorldPosition()`, which is RENDER space — the FP64 origin
      // rebase offsets the scene, so after the first rebase every distance was
      // off by the rebase offset (hundreds of km) and `every` pinned to 3.
      // Nearby actors animated at 20 Hz from then on. The posed absolute
      // position is already known, and skipping the matrix walk is cheaper too.
      const dx = a.absX - playerX, dz = a.absZ - playerZ;
      const d2 = dx * dx + dz * dz;
      const every = d2 < 15 * 15 ? 1 : d2 < 30 * 30 ? 2 : 3;
      a.bankedDt = (a.bankedDt ?? 0) + dt;
      if (every > 1 && (this.frame + a.phase) % every !== 0) continue;
      a.mixer.update(Math.min(0.25, a.bankedDt));
      a.bankedDt = 0;
      // HEAD LAYER (2026-09-15): after the clip posed the bones, turn the head toward the
      // look direction about the WORLD up axis (expressed in the bone's parent space — the
      // bone's own axes are whatever the rig's orientation makes them), eased, clamped ±70°.
      // A casual glance is a head turn only; the body keeps its pose.
      if (a.head) {
        const target = a.lookYaw === null ? 0 : headTurn(a.bodyYaw, a.lookYaw);
        a.headYaw += (target - a.headYaw) * HEAD_TURN_EASE;
        if (Math.abs(a.headYaw) > 1e-3 && a.head.parent) {
          _axis.set(0, 1, 0).applyQuaternion(a.head.parent.getWorldQuaternion(_q).invert());
          a.head.rotateOnAxis(_axis, a.headYaw);
          this.stats.heads++;
        }
      }
    }
  }
  private frame = 0;

  /** Diag for probes: actors, their bodies and clips. */
  snapshot(): { actors: number; bodies: number; swaps: number; missingClip: number; posed: number; playing: Array<{ id: string; body: string; clip: string; clips: number }> } {
    return { ...this.stats, playing: [...this.actors.values()].slice(0, 12).map((a) => ({ id: a.id, body: a.bodyKey, clip: a.currentName, clips: a.clips.size })) };
  }

  dispose(): void {
    for (const a of this.actors.values()) { this.group.remove(a.root); a.mixer.stopAllAction(); }
    this.actors.clear(); this.pool.clear(); this.masters.clear(); this.owned.clear();
  }
}
